import { Divider, HorizontalGrid, Text } from '@shopify/polaris';
import { VerticalSpace } from '../../../UI/Reports/VerticalSpace';
import { formatMoney, formatUnits } from '../efficacy/formatters';

const ROTATION_NUMBER = '#4A4A4F';
const SUBTLE = '#6D7175';
const STRONG = '#202223';
const RANK_WATERMARK = '#D1D5DB';
const PLACEHOLDER_BG = '#F6F6F7';

const RankNumber = ({ n }) => (
  <div
    style={{
      width: 36,
      fontSize: 28,
      fontWeight: 700,
      color: RANK_WATERMARK,
      lineHeight: 1,
      textAlign: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </div>
);

const Stat = ({ label, value }) => (
  <div style={{ fontSize: 12, color: SUBTLE, whiteSpace: 'nowrap' }}>
    <span style={{ color: SUBTLE }}>{label}: </span>
    <span style={{ color: STRONG, fontWeight: 500 }}>{value}</span>
  </div>
);

const SectionTerm = ({ title, subtitle }) => (
  <div>
    <Text variant="headingMd" as="h3">
      {title}
    </Text>
    <Text variant="bodySm" tone="subdued" as="span">
      {subtitle}
    </Text>
  </div>
);

const RankingTable = ({ entries, currency }) => (
  <div>
    {entries.map((row, i) => (
      <div
        key={row.family}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingTop: i === 0 ? 0 : 12,
          paddingBottom: i === entries.length - 1 ? 0 : 12,
          borderBottom: i === entries.length - 1 ? 'none' : '1px solid #F1F2F4',
        }}
      >
        <RankNumber n={i + 1} />
        {row.image ? (
          <img
            src={row.image}
            alt={row.family}
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
          />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 6, backgroundColor: PLACEHOLDER_BG, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: STRONG, marginBottom: 4 }}>
            {row.productId ? (
              <a className="efftable-link" href={`/products/${row.productId}`} target="_blank" rel="noopener noreferrer">
                {row.family}
              </a>
            ) : (
              row.family
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
            <Stat label="Compra" value={`${formatUnits(row.buyUnits)} uds`} />
            <Stat label="Venta" value={`${formatUnits(row.sellUnits)} uds`} />
            <Stat label="Stock" value={`${formatUnits(row.remainUnits)} uds`} />
            <Stat label="Valor almacén" value={formatMoney(row.remainValueCI, currency)} />
          </div>
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: ROTATION_NUMBER,
            minWidth: 70,
            textAlign: 'right',
            flexShrink: 0,
          }}
        >
          {row.rotation != null ? `${row.rotation.toFixed(2)}×` : '—'}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Top/bottom 5 series por rotación. Equivalente al
 * `EfficacyHighlights` del informe de Eficacia, pero adaptado:
 *   - Rankea por `rotation` (anualizada) en lugar de `efficacyTotal`.
 *   - Stats inline: Compra · Venta · Stock · Valor almacén.
 *   - Valor a la derecha en formato "X.XX×" (rotación anual).
 *
 * Filtra rows sin compras o sin rotación finita — sin esos datos no
 * tiene sentido rankear.
 */
export const ReplenishmentHighlights = ({ rows, currency }) => {
  const valid = (rows || []).filter(
    (r) => Number(r.lifetimeBuyUnits) > 0 && r.rotation != null && Number.isFinite(Number(r.rotation)),
  );
  if (valid.length === 0) return null;

  const sorted = [...valid].sort((a, b) => Number(b.rotation) - Number(a.rotation));
  const top5 = sorted.slice(0, 5);
  const topSet = new Set(top5);
  const bottom5 = sorted.slice().reverse().filter((r) => !topSet.has(r)).slice(0, 5);

  const Pair = ({ term, description }) => (
    <HorizontalGrid columns={{ xs: '1fr', md: '1fr 1fr' }} gap="4">
      <div style={{ paddingLeft: 24 }}>{term}</div>
      <div>{description}</div>
    </HorizontalGrid>
  );

  return (
    <div>
      <Pair
        term={<SectionTerm title="Más rotación" subtitle={`Top ${top5.length} por rotación anual`} />}
        description={<RankingTable entries={top5} currency={currency} />}
      />
      {bottom5.length > 0 && (
        <>
          <VerticalSpace />
          <Divider />
          <VerticalSpace />
          <Pair
            term={<SectionTerm title="Menos rotación" subtitle={`Las ${bottom5.length} con menor rotación`} />}
            description={<RankingTable entries={bottom5} currency={currency} />}
          />
        </>
      )}
    </div>
  );
};
